import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';

import ActionMenu from 'components/ActionMenu';
import { ButtonV3 } from 'components/ComponentV2';
import Icon from 'components/Icon';
import { useCandidate } from 'hooks/useCandidate';
import ResponseModal from 'pages/candidate/applications/ResponseModal/ResponseModal.component';
import {
  ApplicationResponseModalEnum,
  CandidateApplicationType,
} from 'lib/models/candidate-applications';
import { useAuth } from 'lib/providers/AuthProvider';
import { signInModalInstance } from '../SignInModal';

import styles from './GPTActionMenu.module.scss';

interface GPTActionMenuProps {
  application: CandidateApplicationType;
  showIcon?: boolean;
  iconClassName?: string;
  listClassName?: string;
  menuButtonClassName?: string;
}

export const GPTActionMenu: React.FC<GPTActionMenuProps> = (
  props: GPTActionMenuProps
) => {
  const {
    application,
    showIcon,
    iconClassName,
    listClassName,
    menuButtonClassName,
  } = props;
  const applicationId = application.id;
  const { isCandidate } = useAuth();
  const { redirectToCoverLetterBuilder } = useCandidate();
  const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ApplicationResponseModalEnum>();

  const toggleResponseModal = useCallback(
    (type?: ApplicationResponseModalEnum) => {
      setShowResponseModal((flag) => !flag);
      setModalType(type);
    },
    [setModalType]
  );

  const handleQuestionsRepliesClick = useCallback(async () => {
    try {
      if (!isCandidate) await signInModalInstance();
      toggleResponseModal(ApplicationResponseModalEnum.questionResponse);
    } catch (error) {
      console.error(error);
    }
  }, [isCandidate, toggleResponseModal, signInModalInstance]);

  const handleWriteCoverLetterClick = useCallback(async () => {
    try {
      if (!isCandidate) await signInModalInstance();
      redirectToCoverLetterBuilder(applicationId);
    } catch (error) {
      console.error(error);
    }
  }, [
    isCandidate,
    signInModalInstance,
    applicationId,
    redirectToCoverLetterBuilder,
  ]);

  const actionMenuOptions = useMemo(() => {
    return [
      {
        id: '1',
        label: 'Get questions replies',
        onClick: handleQuestionsRepliesClick,
      },
      {
        id: '2',
        label: 'Write cover letter',
        onClick: handleWriteCoverLetterClick,
      },
    ];
  }, [isCandidate]);

  return (
    <>
      <ActionMenu
        options={actionMenuOptions}
        className={styles.gptActionMenu}
        actionElement={
          showIcon ? (
            <div className={iconClassName}>
              <Icon
                iconName="icon_magic-wand"
                size="xmediumplus"
                className={styles.actionIcon}
              />
            </div>
          ) : (
            <ButtonV3
              className={styles.actionButton}
              size="small"
              color="secondary"
              endIcon="icon_arrow-down"
            >
              Write applications
            </ButtonV3>
          )
        }
        listClassName={cx(styles.gptActionMenuList, listClassName)}
        menuButtonClassName={cx(
          styles.gptActionMenuButton,
          menuButtonClassName
        )}
      />
      <ResponseModal
        isOpen={showResponseModal}
        modalType={modalType}
        onClose={toggleResponseModal}
        application={application}
      />
    </>
  );
};
